import type { QuestionDefinition, QuestionCategory } from '../types/checkin';

export const questionLibrary: QuestionDefinition[] = [
  // Mood & Energy (6 questions)
  {
    id: 'morning-mood',
    text: 'How are you feeling this morning?',
    type: 'emoji-scale',
    category: 'mood-energy',
    scaleLabels: { low: 'Struggling', high: 'Energized' },
    defaultForTemplate: 'daily-intentions',
  },
  {
    id: 'morning-energy',
    text: 'How is your energy level?',
    type: 'emoji-scale',
    category: 'mood-energy',
    scaleLabels: { low: 'Exhausted', high: 'Energized' },
    defaultForTemplate: 'step-11',
  },
  {
    id: 'sleep-quality',
    text: 'How well did you sleep last night?',
    type: 'scale-1-10',
    category: 'mood-energy',
    scaleLabels: { low: 'Terrible', high: 'Excellent' },
  },
  {
    id: 'trigger-check',
    text: 'Are you feeling triggered or vulnerable today?',
    type: 'yes-no',
    category: 'mood-energy',
  },
  {
    id: 'emotional-state',
    text: 'What emotion are you feeling most right now?',
    type: 'text',
    category: 'mood-energy',
  },
  {
    id: 'physical-health',
    text: 'How is your physical health today?',
    type: 'emoji-scale',
    category: 'mood-energy',
    scaleLabels: { low: 'Poor', high: 'Great' },
  },

  // Gratitude (4 questions)
  {
    id: 'morning-gratitude',
    text: "What are 3 things you're grateful for today?",
    type: 'text-list',
    category: 'gratitude',
    subtext: 'List at least 3 things',
    defaultForTemplate: 'daily-intentions',
  },
  {
    id: 'gratitude-person',
    text: 'Who are you grateful for today, and why?',
    type: 'text',
    category: 'gratitude',
  },
  {
    id: 'yesterday-good',
    text: 'What went well yesterday?',
    type: 'textarea',
    category: 'gratitude',
  },
  {
    id: 'today-blessing',
    text: 'What are you looking forward to today?',
    type: 'text',
    category: 'gratitude',
  },

  // Intentions (5 questions)
  {
    id: 'main-intention',
    text: 'What is one thing you want to accomplish today?',
    type: 'text',
    category: 'intentions',
    defaultForTemplate: 'daily-intentions',
  },
  {
    id: 'anticipated-challenges',
    text: 'Are you anticipating any challenges today?',
    type: 'yes-no',
    category: 'intentions',
    subtext: 'This helps you prepare mentally',
    defaultForTemplate: 'daily-intentions',
  },
  {
    id: 'how-to-handle-challenges',
    text: 'How will you handle challenges if they arise?',
    type: 'textarea',
    category: 'intentions',
  },
  {
    id: 'priority-action',
    text: "What's your #1 priority today?",
    type: 'text',
    category: 'intentions',
  },
  {
    id: 'realistic-goals',
    text: 'Are your goals for today realistic?',
    type: 'yes-no',
    category: 'intentions',
  },

  // Commitments (6 questions)
  {
    id: 'pray-today',
    text: 'Will you pray today?',
    type: 'yes-no',
    category: 'commitments',
  },
  {
    id: 'meditate-today',
    text: 'Will you meditate today?',
    type: 'yes-no',
    category: 'commitments',
  },
  {
    id: 'read-recovery-literature',
    text: 'Will you read recovery literature?',
    type: 'yes-no',
    category: 'commitments',
  },
  {
    id: 'attend-meeting',
    text: 'Will you attend a meeting today?',
    type: 'yes-no',
    category: 'commitments',
  },
  {
    id: 'call-sponsor',
    text: 'Will you reach out to your sponsor?',
    type: 'yes-no',
    category: 'commitments',
  },
  {
    id: 'exercise-plan',
    text: 'Do you have a plan to exercise or move your body?',
    type: 'yes-no',
    category: 'commitments',
  },

  // Spiritual (5 questions)
  {
    id: 'morning-prayer-done',
    text: 'Have you taken time for prayer this morning?',
    type: 'yes-no',
    category: 'spiritual',
    defaultForTemplate: 'step-11',
  },
  {
    id: 'meditation-done',
    text: 'Have you meditated today?',
    type: 'yes-no',
    category: 'spiritual',
    subtext: 'Even 5 minutes counts',
    defaultForTemplate: 'step-11',
  },
  {
    id: 'asking-hp-guidance',
    text: 'What guidance are you seeking from your Higher Power today?',
    type: 'textarea',
    category: 'spiritual',
    defaultForTemplate: 'step-11',
  },
  {
    id: 'morning-reading',
    text: 'What did you read from recovery literature today?',
    type: 'text',
    category: 'spiritual',
    subtext: 'Daily reflection, Big Book, etc.',
    defaultForTemplate: 'step-11',
  },
  {
    id: 'ask-hp-help',
    text: 'What do you need help from your Higher Power with today?',
    type: 'textarea',
    category: 'spiritual',
    defaultForTemplate: 'daily-intentions',
  },

  // Health (4 questions)
  {
    id: 'sleep-hours',
    text: 'How many hours did you sleep?',
    type: 'scale-1-10',
    category: 'health',
    scaleLabels: { low: '0-3 hours', high: '9+ hours' },
  },
  {
    id: 'eating-well',
    text: 'Are you eating healthy meals today?',
    type: 'yes-no',
    category: 'health',
  },
  {
    id: 'hydration',
    text: 'Are you staying hydrated?',
    type: 'yes-no',
    category: 'health',
  },
  {
    id: 'self-care-plan',
    text: "What's your self-care plan for today?",
    type: 'text',
    category: 'health',
  },

  // Relationships (4 questions)
  {
    id: 'difficult-conversations',
    text: 'Do you need to have any difficult conversations?',
    type: 'yes-no',
    category: 'relationships',
  },
  {
    id: 'amends-reminders',
    text: 'Are there any amends you need to make?',
    type: 'yes-no',
    category: 'relationships',
  },
  {
    id: 'connection-plan',
    text: 'Who will you connect with today?',
    type: 'text',
    category: 'relationships',
  },
  {
    id: 'boundaries-needed',
    text: 'Do you need to set any boundaries today?',
    type: 'yes-no',
    category: 'relationships',
  },

  // Sobriety (3 questions)
  {
    id: 'sobriety-affirmation',
    text: 'Today I choose...',
    type: 'text',
    category: 'sobriety',
  },
  {
    id: 'sobriety-commitment',
    text: 'I commit to my sobriety by...',
    type: 'text',
    category: 'sobriety',
  },
  {
    id: 'sobriety-plan',
    text: "What's your sobriety plan for today?",
    type: 'textarea',
    category: 'sobriety',
  },
];

// Template presets
export const templates = {
  'daily-intentions': questionLibrary.filter(
    (q) => q.defaultForTemplate === 'daily-intentions'
  ),
  'step-11': questionLibrary.filter((q) => q.defaultForTemplate === 'step-11'),
};

// Helper functions
export function getQuestionById(id: string): QuestionDefinition | undefined {
  return questionLibrary.find((q) => q.id === id);
}

export function getQuestionsByIds(ids: string[]): QuestionDefinition[] {
  return ids.map((id) => getQuestionById(id)).filter(Boolean) as QuestionDefinition[];
}

export function getQuestionsByCategory(
  category: QuestionCategory
): QuestionDefinition[] {
  return questionLibrary.filter((q) => q.category === category);
}
